import { useEffect, useState } from 'react'
import { adminApi } from '../../api'
import { useToast } from '../../context/ToastContext'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Toggle } from '../../components/ui/Toggle'
import { Spinner } from '../../components/ui/Spinner'

interface FormState {
  appName: string
  backgroundImage: string
  defaultTheme: 'dark' | 'light'
  localAuthEnabled: boolean
  oidc: {
    enabled: boolean
    providerName: string
    issuer: string
    clientId: string
    clientSecret: string
    redirectUri: string
    scopes: string
  }
  turn: { stunUrls: string; turnUrl: string; turnUsername: string; turnPassword: string }
}

export function SettingsPage() {
  const [form, setForm] = useState<FormState | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const toast = useToast()

  useEffect(() => {
    void load()
  }, [])

  async function load() {
    try {
      const { settings } = await adminApi.getSettings()
      setForm({
        appName: settings.app_name || 'family-vc',
        backgroundImage: settings.background_image || '',
        defaultTheme: settings.default_theme === 'light' ? 'light' : 'dark',
        localAuthEnabled: settings.local_auth_enabled !== 'false',
        oidc: {
          enabled: settings.oidc_enabled === 'true',
          providerName: settings.oidc_provider_name || 'SSO',
          issuer: settings.oidc_issuer || '',
          clientId: settings.oidc_client_id || '',
          clientSecret: settings.oidc_client_secret || '',
          redirectUri: settings.oidc_redirect_uri || '',
          scopes: settings.oidc_scopes || 'openid email profile',
        },
        turn: {
          stunUrls: settings.stun_urls || '',
          turnUrl: settings.turn_url || '',
          turnUsername: settings.turn_username || '',
          turnPassword: settings.turn_password || '',
        },
      })
    } catch {
      toast.push('Einstellungen konnten nicht geladen werden', 'error')
    }
  }

  async function save() {
    if (!form) return
    if (form.oidc.enabled && form.oidc.redirectUri && !form.oidc.redirectUri.endsWith('/callback')) {
      toast.push('Redirect URI muss mit /callback enden', 'error')
      return
    }
    setSubmitting(true)
    try {
      const payload = {
        appName: form.appName,
        backgroundImage: form.backgroundImage,
        defaultTheme: form.defaultTheme,
        localAuthEnabled: form.localAuthEnabled,
        oidc: {
          enabled: form.oidc.enabled,
          providerName: form.oidc.providerName,
          issuer: form.oidc.issuer,
          clientId: form.oidc.clientId,
          // Only send secret if it was changed (not the masked placeholder).
          ...(form.oidc.clientSecret && form.oidc.clientSecret !== '********'
            ? { clientSecret: form.oidc.clientSecret }
            : {}),
          redirectUri: form.oidc.redirectUri,
          scopes: form.oidc.scopes,
        },
        turn: {
          stunUrls: form.turn.stunUrls,
          turnUrl: form.turn.turnUrl,
          turnUsername: form.turn.turnUsername,
          ...(form.turn.turnPassword && form.turn.turnPassword !== '********'
            ? { turnPassword: form.turn.turnPassword }
            : {}),
        },
      }
      await adminApi.updateSettings(payload)
      toast.push('Gespeichert', 'success')
      await load()
    } catch {
      toast.push('Speichern fehlgeschlagen', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  if (!form) return <Spinner />

  return (
    <div className="page">
      <div className="page-head">
        <h1>Einstellungen</h1>
        <Button onClick={save} loading={submitting}>
          Speichern
        </Button>
      </div>

      <section className="section">
        <h2>Allgemein</h2>
        <div className="grid-2">
          <Input
            label="App-Name"
            value={form.appName}
            onChange={(e) => setForm({ ...form, appName: e.target.value })}
          />
          <Select
            label="Standard-Theme"
            value={form.defaultTheme}
            onValueChange={(v) => setForm({ ...form, defaultTheme: v === 'light' ? 'light' : 'dark' })}
            options={[
              { value: 'dark', label: 'Dunkel' },
              { value: 'light', label: 'Hell' },
            ]}
          />
        </div>
        <Input
          label="Hintergrundbild URL"
          hint="Gilt für Login-Seite und alle Seiten. Leer lassen um zu deaktivieren."
          type="url"
          placeholder="https://example.com/background.jpg"
          value={form.backgroundImage}
          onChange={(e) => setForm({ ...form, backgroundImage: e.target.value })}
        />
        <Toggle
          label="Lokale Anmeldung aktivieren"
          checked={form.localAuthEnabled}
          onCheckedChange={(v) => setForm({ ...form, localAuthEnabled: v })}
        />
      </section>

      <section className="section">
        <h2>OIDC / SSO</h2>
        <Toggle
          label="OIDC aktivieren"
          checked={form.oidc.enabled}
          onCheckedChange={(v) => setForm({ ...form, oidc: { ...form.oidc, enabled: v } })}
        />
        {form.oidc.enabled && (
          <div className="grid-2">
            <Input
              label="Provider-Name"
              value={form.oidc.providerName}
              onChange={(e) => setForm({ ...form, oidc: { ...form.oidc, providerName: e.target.value } })}
            />
            <Input
              label="Issuer URL"
              value={form.oidc.issuer}
              onChange={(e) => setForm({ ...form, oidc: { ...form.oidc, issuer: e.target.value } })}
            />
            <Input
              label="Client ID"
              value={form.oidc.clientId}
              onChange={(e) => setForm({ ...form, oidc: { ...form.oidc, clientId: e.target.value } })}
            />
            <Input
              type="password"
              label="Client Secret"
              placeholder={form.oidc.clientSecret === '********' ? 'unverändert' : ''}
              value={form.oidc.clientSecret === '********' ? '' : form.oidc.clientSecret}
              onChange={(e) => setForm({ ...form, oidc: { ...form.oidc, clientSecret: e.target.value } })}
            />
            <Input
              label="Redirect URI"
              hint="Muss mit /callback enden"
              value={form.oidc.redirectUri}
              onChange={(e) => setForm({ ...form, oidc: { ...form.oidc, redirectUri: e.target.value } })}
            />
            <Input
              label="Scopes"
              value={form.oidc.scopes}
              onChange={(e) => setForm({ ...form, oidc: { ...form.oidc, scopes: e.target.value } })}
            />
          </div>
        )}
      </section>

      <section className="section">
        <h2>TURN / STUN</h2>
        <div className="grid-2">
          <Input
            label="STUN URLs"
            value={form.turn.stunUrls}
            onChange={(e) => setForm({ ...form, turn: { ...form.turn, stunUrls: e.target.value } })}
          />
          <Input
            label="TURN URL"
            value={form.turn.turnUrl}
            onChange={(e) => setForm({ ...form, turn: { ...form.turn, turnUrl: e.target.value } })}
          />
          <Input
            label="TURN Benutzername"
            value={form.turn.turnUsername}
            onChange={(e) => setForm({ ...form, turn: { ...form.turn, turnUsername: e.target.value } })}
          />
          <Input
            type="password"
            label="TURN Passwort"
            placeholder={form.turn.turnPassword === '********' ? 'unverändert' : ''}
            value={form.turn.turnPassword === '********' ? '' : form.turn.turnPassword}
            onChange={(e) => setForm({ ...form, turn: { ...form.turn, turnPassword: e.target.value } })}
          />
        </div>
      </section>
    </div>
  )
}
